import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, catchError, distinctUntilChanged, firstValueFrom, map, Observable, of, Subject, switchMap, takeUntil, timeout } from 'rxjs';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../../../environments/environment';
import { AuthModeService } from './auth-mode.service';
import { LegacySessionService } from '../legacy-session.service';
import { OIDC_CONFIG_ID, OIDC_STORAGE_PREFIX, OidcSession, OidcStorageService } from './oidc-storage.service';
import { OidcSessionCoordinationService, OidcSessionEndedError, SessionControl } from './oidc-session-coordination.service';

export { OidcSessionEndedError } from './oidc-session-coordination.service';

class OidcAccessDeniedError extends Error {}

export interface OidcSessionError {
  kind: 'access-denied' | 'retryable';
  message: string;
}

@Injectable({ providedIn: 'root' })
export class OidcSessionService {
  readonly token$ = new BehaviorSubject<string | null>(null);
  readonly permissionNames$: Observable<readonly string[]> = this.token$.pipe(
    switchMap(token => token ? this.oidc.getPayloadFromAccessToken(false, OIDC_CONFIG_ID).pipe(
      map(payload => {
        const permissions = payload?.permission;
        if (typeof permissions === 'string') return [permissions];
        return Array.isArray(permissions) ? permissions.filter((value): value is string => typeof value === 'string') : [];
      }),
      catchError(() => of([])),
    ) : of([])),
  );
  readonly ended$ = new Subject<void>();
  readonly cancel$ = new Subject<void>();
  readonly error$ = new BehaviorSubject<OidcSessionError | null>(null);
  private readonly transactionKey = OIDC_STORAGE_PREFIX + 'transactionEpoch';
  private readonly returnKey = OIDC_STORAGE_PREFIX + 'returnUrl';
  private readonly logoutOwnerKey = OIDC_STORAGE_PREFIX + 'logoutOwner';
  private session: OidcSession | null = null;
  private initialization?: Promise<void>;
  // Stopping is irreversible for this document. Redirect flags only deduplicate
  // attempts; a failed logout can be retried while the document stays stopped.
  private sessionStopped = false;
  private signInRedirectPending = false;
  private logoutRedirectPending = false;
  private refreshRejected = false;
  private retiredLegacySession = false;

  constructor(
    private oidc: OidcSecurityService,
    private storage: OidcStorageService,
    private mode: AuthModeService,
    private zone: NgZone,
    private legacy: LegacySessionService,
    private coordination: OidcSessionCoordinationService,
  ) {
    window.addEventListener('storage', event => {
      if (event.key === this.coordination.controlKey && this.mode.useOidc()) {
        this.zone.run(() => this.checkSessionEpoch());
      }
    });
    window.addEventListener('pageshow', event => {
      if (event.persisted && this.mode.useOidc()) window.location.reload();
    });
    this.zone.runOutsideAngular(() => {
      window.setInterval(() => {
        if (document.visibilityState === 'visible') this.renewWhenNeeded();
      }, 15000);
      window.addEventListener('focus', () => this.renewWhenNeeded());
    });
  }

  get accessToken(): string | null {
    if (this.sessionStopped || this.session?.epoch !== this.coordination.readControl().epoch) return null;
    return this.token$.value;
  }

  get authenticated(): boolean { return !!this.accessToken; }
  get busy(): boolean { return this.signInRedirectPending || this.sessionStopped || this.logoutRedirectPending; }

  private get canAcquireToken(): boolean {
    return !this.busy && !this.error$.value && this.mode.useOidc();
  }

  hasPermission$(permission: string): Observable<boolean> {
    return this.permissionNames$.pipe(
      map(permissions => permissions.includes(permission)),
      distinctUntilChanged(),
    );
  }

  async clearForLegacySession(expectedLegacyEpoch?: string): Promise<void> {
    this.sessionStopped = true;
    this.cancel$.next();
    if (expectedLegacyEpoch === undefined) this.storage.clearSession(true);
    await this.coordination.exclusive(async () => {
      if (expectedLegacyEpoch !== undefined && this.legacy.epoch !== expectedLegacyEpoch) {
        throw new OidcSessionEndedError();
      }
      const control = this.coordination.readControl();
      control.epoch = crypto.randomUUID();
      delete control.subject;
      this.coordination.writeControl(control);
      this.clearSession(true);
    });
  }

  reportError(message: string): void { this.error$.next({ kind: 'retryable', message }); }

  recordRefreshRejection(): void { this.refreshRejected = true; }

  initialize(): Promise<void> {
    return this.initialization ||= this.start().catch(error => {
      if (error instanceof OidcSessionEndedError || this.sessionStopped) return;
      this.fail(error, 'Unable to restore your sign-in session. Please try again.');
    });
  }

  private async start(): Promise<void> {
    // Do not import the old adapter's shared PKCE/token record into the new flow.
    const oldConfigKey = `0-${environment.oidcConfig.clientId}`;
    localStorage.removeItem(oldConfigKey);
    sessionStorage.removeItem(oldConfigKey);
    const url = new URL(window.location.href);
    const callback = url.searchParams.has('state') &&
      (url.searchParams.has('code') || url.searchParams.has('error'));
    if (!callback) await this.completeLogoutCallback(url);

    await this.coordination.waitForLogout();
    await this.coordination.claimTab(() => {
      this.storage.clearSession(false);
      sessionStorage.removeItem(this.transactionKey);
    });
    await this.coordination.exclusive(async () => {
      const epoch = this.coordination.readControl().epoch;
      if (callback) await this.completeSignInCallback(url, epoch);
      else await this.restoreSession(epoch);
    });
  }

  private async completeLogoutCallback(url: URL): Promise<void> {
    const logoutOwner = sessionStorage.getItem(this.logoutOwnerKey);
    const postLogoutPath = new URL(environment.oidcConfig.postLogoutRedirectUri).pathname;
    if (url.pathname !== postLogoutPath || !logoutOwner || url.searchParams.get('state') !== logoutOwner) return;
    await this.coordination.exclusive(async () => {
      const control = this.coordination.readControl();
      if (control.logout?.state === logoutOwner) {
        delete control.logout;
        this.coordination.writeControl(control);
      }
      sessionStorage.removeItem(this.logoutOwnerKey);
    });
    window.history.replaceState(window.history.state, '', '/');
  }

  // Callback adoption and restoration both run under the shared session lock.
  private async completeSignInCallback(url: URL, epoch: string): Promise<void> {
    if (sessionStorage.getItem(this.transactionKey) !== epoch) {
      this.storage.clearSession(false);
      this.cleanCallbackUrl();
      return;
    }
    try {
      // Read pending state before the library processes and clears an error callback.
      const deniedState = url.searchParams.get('error') === 'access_denied' && !url.searchParams.has('code')
        ? await this.run(this.oidc.getState(OIDC_CONFIG_ID)) : null;
      const result = await this.run(this.oidc.checkAuth(url.href, OIDC_CONFIG_ID));
      this.assertEpoch(epoch);
      if (!result.isAuthenticated) {
        if (deniedState && url.searchParams.get('state') === deniedState) {
          throw new OidcAccessDeniedError();
        }
        throw new Error('The sign-in response was not accepted.');
      }
      const claims = await firstValueFrom(this.oidc.getPayloadFromIdToken(false, OIDC_CONFIG_ID));
      const control = this.coordination.readControl();
      if (control.subject && control.subject !== claims.sub) {
        control.epoch = crypto.randomUUID();
      }
      control.subject = claims.sub;
      this.coordination.writeControl(control);
      this.accept(this.storage.capture(control.epoch, claims.remember_me === true || claims.remember_me === 'true'));
    } catch (error) {
      if (this.coordination.readControl().epoch !== epoch) {
        this.storage.clearSession(false);
        return;
      }
      throw error;
    } finally {
      sessionStorage.removeItem(this.transactionKey);
      this.cleanCallbackUrl();
    }
  }

  private async restoreSession(epoch: string): Promise<void> {
    const saved = this.storage.readSession();
    if (!saved || saved.epoch !== epoch) {
      this.storage.clearSession(false);
      return;
    }
    this.storage.hydrate(saved);
    const result = await this.run(this.oidc.checkAuth(window.location.origin, OIDC_CONFIG_ID));
    this.assertEpoch(epoch);
    this.session = saved;
    if (result.isAuthenticated && !this.expiresSoon(saved)) this.accept(saved);
    else await this.refreshInsideLock(saved);
  }

  async ensureAuthenticated(returnUrl: string): Promise<boolean> {
    await this.initialize();
    if (this.error$.value || this.sessionStopped) return false;
    try {
      if (await this.getToken()) return true;
      await this.authorize(returnUrl);
    } catch (error) {
      if (!(error instanceof OidcSessionEndedError)) this.fail(error, 'Unable to sign in. Please try again.');
    }
    return false;
  }

  async authorize(returnUrl = '/'): Promise<void> {
    if (this.busy || this.error$.value) return;
    this.signInRedirectPending = true;
    sessionStorage.setItem(this.returnKey, this.safeReturnUrl(returnUrl));
    try {
      await this.coordination.waitForLogout();
      await this.coordination.exclusive(async () => {
        const control = this.coordination.readControl();
        if (control.logout) throw new OidcSessionEndedError();
        const epoch = control.epoch;
        sessionStorage.setItem(this.transactionKey, epoch);
        await this.run(this.oidc.preloadAuthWellKnownDocument(OIDC_CONFIG_ID));
        this.assertEpoch(epoch);
        this.oidc.authorize(OIDC_CONFIG_ID, {
          urlHandler: url => {
            const current = this.coordination.readControl();
            if (current.epoch === epoch && !current.logout) window.location.assign(url);
          },
        });
      });
    } catch (error) {
      this.signInRedirectPending = false;
      throw error;
    }
  }

  getToken(rejectedToken?: string): Promise<string | null> {
    if (!this.canAcquireToken) return Promise.resolve(null);
    return this.coordination.exclusive(async () => {
      this.checkSessionEpoch();
      if (!this.canAcquireToken || this.coordination.readControl().logout) return null;
      const saved = this.storage.readSession();
      if (!saved || saved.epoch !== this.coordination.readControl().epoch) return null;
      this.storage.hydrate(saved);
      // Another tab/request may have already replaced the rejected token.
      if (!this.expiresSoon(saved) && saved.tokens.authzData !== rejectedToken) {
        this.accept(saved);
        return saved.tokens.authzData;
      }
      return this.refreshInsideLock(saved);
    });
  }

  private async refreshInsideLock(saved: OidcSession): Promise<string | null> {
    if (!saved.tokens.authnResult.refresh_token) {
      this.requireNewLogin();
      return null;
    }
    try {
      this.refreshRejected = false;
      const result: LoginResponse = await this.run(this.oidc.forceRefreshSession(undefined, OIDC_CONFIG_ID));
      this.assertEpoch(saved.epoch);
      if (!result.isAuthenticated || !result.accessToken) throw new Error('Session refresh was not accepted.');
      const renewed = this.storage.capture(saved.epoch, saved.remember);
      this.accept(renewed);
      return renewed.tokens.authzData;
    } catch (error) {
      if (this.coordination.readControl().epoch !== saved.epoch || this.sessionStopped) throw new OidcSessionEndedError();
      // The library wraps refresh failures as Error. Do not destroy a durable
      // refresh token on an ambiguous network failure or retry the grant blindly.
      this.storage.hydrate(saved);
      if (this.refreshRejected || /invalid_grant|no refresh token|Session refresh was not accepted/i.test(String(error))) {
        this.requireNewLogin();
        return null;
      }
      // Publish recovery before releasing the lock to stop already-queued callers.
      this.reportError('Unable to renew your session. Please try again.');
      throw error;
    }
  }

  logout(): void {
    if (this.sessionStopped) return;
    this.sessionStopped = true;
    this.error$.next(null);
    this.cancel$.next();
    this.token$.next(null);
    this.ended$.next();
    this.beginLogout();
  }

  private beginLogout(pending?: SessionControl): void {
    if (this.logoutRedirectPending) return;
    this.logoutRedirectPending = true;
    this.error$.next(null);
    void this.coordination.exclusive(async () => {
      let control = this.coordination.readControl();
      if (pending) {
        this.coordination.assertLogout(pending);
      } else {
        if (control.logout) {
          this.clearSession(false);
          window.location.assign('/');
          return;
        }
        const state = crypto.randomUUID();
        control = { epoch: crypto.randomUUID(), logout: { state, startedAt: Date.now() } };
        this.coordination.writeControl(control);
        localStorage.removeItem(this.storage.sessionKey);
        sessionStorage.removeItem(this.storage.sessionKey);
        sessionStorage.removeItem(this.returnKey);
      }
      sessionStorage.setItem(this.logoutOwnerKey, control.logout.state);
      await this.navigateToLogout(control);
    }).catch(error => {
      this.logoutRedirectPending = false;
      if (error instanceof OidcSessionEndedError) {
        // Another tab completed this logout. Restore current state, not the old attempt.
        window.location.reload();
        return;
      }
      this.fail(error, 'Sign out could not be completed. Please try again.');
    });
  }

  private async navigateToLogout(control: SessionControl): Promise<void> {
    this.coordination.assertLogout(control);
    let target: string | undefined;
    try {
      await firstValueFrom(this.oidc.preloadAuthWellKnownDocument(OIDC_CONFIG_ID).pipe(timeout(20000)));
      this.coordination.assertLogout(control);
      await firstValueFrom(this.oidc.logoff(OIDC_CONFIG_ID, {
        customParams: { state: control.logout.state, client_id: environment.oidcConfig.clientId },
        urlHandler: url => { target = url; },
      }).pipe(timeout(20000)));
      this.coordination.assertLogout(control);
    } catch (error) {
      this.coordination.assertLogout(control);
      throw error;
    }
    if (!target) throw new Error('The identity provider did not provide a logout endpoint.');
    this.storage.clearSession(false);
    window.location.assign(target);
  }

  retry(): void {
    const control = this.coordination.readControl();
    if (control.logout) {
      // Explicit recovery only: never assume a timed-out server logout finished.
      this.beginLogout(control);
    } else {
      window.location.reload();
    }
  }

  private accept(session: OidcSession): void {
    if (!this.retiredLegacySession) {
      this.legacy.retire();
      this.retiredLegacySession = true;
    }
    this.session = session;
    if (this.token$.value !== session.tokens.authzData) {
      this.zone.run(() => this.token$.next(session.tokens.authzData));
    }
  }

  private clearSession(shared: boolean): void {
    this.cancel$.next();
    this.oidc.logoffLocal(OIDC_CONFIG_ID);
    this.storage.clearSession(shared);
    this.session = null;
    this.token$.next(null);
  }

  private requireNewLogin(): void {
    this.clearSession(true);
    // Clearing cancels queued API requests, so renewal itself owns re-entry.
    void this.authorize(window.location.pathname + window.location.search + window.location.hash)
      .catch(error => this.fail(error, 'Unable to sign in. Please try again.'));
  }

  private checkSessionEpoch(): void {
    if (!this.sessionStopped && this.session && this.session.epoch !== this.coordination.readControl().epoch) {
      this.sessionStopped = true;
      this.clearSession(false);
      this.ended$.next();
      // The next document waits for the initiating tab's IdP logout callback.
      window.location.assign('/');
    }
  }

  private renewWhenNeeded(): void {
    if (!this.mode.useOidc() || !this.session || this.sessionStopped || this.signInRedirectPending || this.error$.value) return;
    this.zone.run(() => {
      void this.getToken().then(token => {
        if (!token && !this.sessionStopped) return this.authorize(window.location.pathname + window.location.search + window.location.hash);
      }).catch(error => {
        if (!(error instanceof OidcSessionEndedError)) this.fail(error, 'Unable to renew your session. Please try again.');
      });
    });
  }

  private run<T>(request: Observable<T>): Promise<T> {
    return firstValueFrom(request.pipe(timeout(25000), takeUntil(this.cancel$)));
  }

  private expiresSoon(session: OidcSession): boolean {
    return !session.tokens.access_token_expires_at || session.tokens.access_token_expires_at <= Date.now() + 30000;
  }

  private assertEpoch(epoch: string): void {
    if (this.sessionStopped) throw new OidcSessionEndedError();
    this.coordination.assertEpoch(epoch);
  }

  private cleanCallbackUrl(): void {
    const target = this.safeReturnUrl(sessionStorage.getItem(this.returnKey) || '/');
    sessionStorage.removeItem(this.returnKey);
    window.history.replaceState(window.history.state, '', target);
  }

  private safeReturnUrl(value: string): string {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname.toLowerCase() === '/login') return '/';
    return url.pathname + url.search + url.hash;
  }

  private fail(error: unknown, message: string): void {
    // Library errors may contain callback URLs. Never print raw auth responses.
    console.warn('[OIDC] Authentication operation failed:', error instanceof Error ? error.name : 'request error');
    if (error instanceof OidcAccessDeniedError) {
      this.error$.next({
        kind: 'access-denied',
        message: 'This account does not have access to the Opogo Web App. Please contact your administrator.',
      });
    } else {
      this.reportError(message);
    }
  }
}
